import type { HumanMember, Member } from "@/features/containers/containers.types";
import { isMemberType } from "@/features/containers/containers.utils";
import type { AiMember } from "@shared/types/supabase.types";

export type MembersRoster = {
  /** Explicit human members of the container (invites without an email are dropped). */
  humanMembers: HumanMember[];
  /** Explicit AI members of the container. */
  aiMembers: AiMember[];
  /** Project humans not yet in the container — the "Add members" picker. */
  availableHumanMembers: HumanMember[];
  /** Project personas not yet in the container — the "Add members" picker. */
  availableAiMembers: AiMember[];
};

/**
 * The one roster derivation behind the manage-members dialog (CR-177): the
 * chat/message header, the document header and the sidebar row menu all feed
 * the dialog from it, so every entry point shows the same members.
 */
export function buildMembersRoster(
  containerMembers: readonly Member[] | undefined,
  projectMembers: readonly Member[] | undefined,
): MembersRoster {
  const container = containerMembers ?? [];
  const project = projectMembers ?? [];

  const users = container.filter(isMemberType("user"));
  const invites = container.filter(isMemberType("invited_user"));
  const ais = container.filter(isMemberType("ai"));

  const userIds = new Set(users.map((m) => m.user_id));
  const inviteIds = new Set(invites.map((m) => m.invite_id));
  const personaIds = new Set(ais.map((m) => m.ai_persona_id));

  return {
    humanMembers: [...users, ...invites.filter((m) => m.invite_email)],
    aiMembers: ais,
    availableHumanMembers: [
      ...project.filter(isMemberType("user")).filter((m) => !userIds.has(m.user_id)),
      ...project
        .filter(isMemberType("invited_user"))
        .filter((m) => !inviteIds.has(m.invite_id) && m.invite_email),
    ],
    availableAiMembers: project
      .filter(isMemberType("ai"))
      .filter((m) => !personaIds.has(m.ai_persona_id)),
  };
}
